import * as THREE from 'three';
import type { CircuitState } from '../circuit/SignalState';
import { Breadboard } from '../components/Breadboard';
import { DIP14Chip } from '../components/DIP14Chip';
import { ToggleSwitch } from '../components/ToggleSwitch';
import { AxialResistor } from '../components/AxialResistor';
import { LEDComponent } from '../components/LED';
import { LiPoBattery } from '../components/LiPoBattery';
import { Wire } from '../components/Wire';
import { CHIP_MODEL } from '../circuit/PinMap';
import type { QualitySettings } from './PerformanceManager';

const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

export class LabScene extends THREE.Group {
  readonly chip = new DIP14Chip('74HC08N');
  readonly k1 = new ToggleSwitch('K1', 'A');
  readonly k2 = new ToggleSwitch('K2', 'B');
  readonly led = new LEDComponent();
  readonly battery = new LiPoBattery();
  readonly wires: Record<string, Wire>;

  constructor(quality: QualitySettings) {
    super();
    this.name = '3D 数字逻辑实验台';
    const board = new Breadboard();
    this.add(board);
    this.chip.position.set(1.3, 0.4, 0);
    this.k1.position.set(-7, 0.3, -1.7);
    this.k2.position.set(-7, 0.3, 2.1);
    const r1 = new AxialResistor('R1', '10kΩ', '输入 A 上拉', [0x6b2d18, 0x111111, 0xe36a18, 0xd4af37]);
    r1.position.set(-3.7, 0.36, -3.7);
    const r2 = new AxialResistor('R2', '10kΩ', '输入 B 上拉', [0x6b2d18, 0x111111, 0xe36a18, 0xd4af37]);
    r2.position.set(-3.7, 0.36, 3.75);
    const r3 = new AxialResistor('R3', '≈2kΩ', 'LED 限流', [0xc72627, 0x111111, 0xc72627, 0xd4af37]);
    r3.position.set(6.2, 0.36, -0.75);
    this.led.position.set(9.2, 0.36, -0.75);
    this.battery.position.set(-8.3, 0.18, 7.7);
    this.battery.rotation.y = -0.08;
    this.add(this.chip, this.k1, this.k2, r1, r2, r3, this.led, this.battery);

    const s = quality.tubeSegments;
    this.wires = {
      vcc: new Wire('VCC', 'VCC 正电源导线', [v(-8.7,.72,6.6),v(-10,.85,5),v(-10,.7,-5),v(3.4,.75,-5),v(3.4,.82,-2.2)], 0xe62942, s),
      gnd: new Wire('GND', 'GND 地线', [v(-7.6,.7,6.6),v(-8.9,.73,5.3),v(-8.9,.7,5),v(3.4,.72,5),v(3.4,.8,2.2)], 0x20252b, s),
      a: new Wire('A', '输入 A 信号导线', [v(-6,.85,-1.7),v(-5.2,1.2,-2.6),v(-1.45,1.15,-2.15)], 0xf0c52f, s),
      b: new Wire('B', '输入 B 信号导线', [v(-6,.85,2.1),v(-4.9,1.4,2.7),v(-0.75,1.18,2.15)], 0x36c776, s),
      pullA: new Wire('pullA', 'R1 至输入 A', [v(-3.7,.95,-3.7),v(-3.7,1.25,-2.6),v(-5.2,1.2,-2.6)], 0xf0c52f, s, false),
      pullB: new Wire('pullB', 'R2 至输入 B', [v(-3.7,.95,3.75),v(-3.7,1.35,2.7),v(-4.9,1.4,2.7)], 0x36c776, s, false),
      y: new Wire('Y', '输出 Y 信号导线', [v(0,.95,2.2),v(3.8,1.35,2.55),v(5.2,1.1,-.75)], 0x2587ed, s),
      ledPath: new Wire('LED-PATH', 'LED 吸电流路径', [v(7.35,.95,-.75),v(8.1,1.25,-.75),v(9.2,1.4,-.75)], 0xff8d27, s),
      ledVcc: new Wire('LED-VCC', 'LED 至 VCC', [v(9.35,1.55,-.75),v(10.4,1.6,-2.2),v(10.4,.75,-5)], 0xe62942, s)
    };
    Object.values(this.wires).forEach((wire) => this.add(wire));
  }

  applyState(state: Readonly<CircuitState>, particleCount: number): void {
    this.chip.setModel(CHIP_MODEL[state.gateType]);
    this.k1.setClosed(state.switchK1);
    this.k2.setClosed(state.switchK2);
    this.led.setOn(state.ledOn);
    this.wires.vcc.setSignal(state.vccConnected);
    this.wires.gnd.setSignal(false);
    this.wires.a.setSignal(state.inputA === 1);
    this.wires.pullA.setSignal(state.inputA === 1);
    this.wires.b.setSignal(state.inputB === 1);
    this.wires.pullB.setSignal(state.inputB === 1);
    this.wires.y.setSignal(state.outputY === 1);
    this.wires.ledPath.setSignal(state.currentPathActive);
    this.wires.ledPath.enableFlow(state.currentPathActive, particleCount);
    this.wires.ledVcc.setSignal(state.vccConnected);
  }

  update(delta: number): void {
    this.k1.update(delta);
    this.k2.update(delta);
    Object.values(this.wires).forEach((wire) => wire.update(delta));
  }
}
